import React, { useCallback, useState, useEffect } from 'react';
import css from './DreamHomePageForm.module.css';
import { useDropzone } from 'react-dropzone';
import { FormattedMessage } from 'react-intl';
import { FirebaseApi } from '../../../util/api';
import moment from 'moment';
import { UploadStatus } from '../../../util/enums';
import {
  bytesToMB,
  extractFileExtension,
  getBaseName,
  showToaster,
} from '../../../util/genericHelpers';
import Icons from '../../../components/Icons/Icons';
import classNames from 'classnames';

const MediaUploadField = ({ input: { onChange, value }, isFinalReviewTab }) => {
  // Log to verify values passed via the Field extra prop.

  const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20 MB
  const currentDate = moment().format('MM-DD-YYYY hh:mm:ss');

  // Local state tracks both new uploads and pre-existing files.
  const [uploadItems, setUploadItems] = useState([]);

  // On mount (or whenever final-form field "value" changes),
  // if there are pre-uploaded files, initialize uploadItems accordingly.
  useEffect(() => {
    if (Array.isArray(value) && value.length > 0) {
      const preUploaded = value.map(item => ({
        file: null, // there is no raw File object for pre-existing uploads
        // Use the provided file data:
        fileName: item.name || item.fileName,
        fileLink: item.fileLink,
        key: item.key,
        progress: 100,
        status: UploadStatus.DONE,
        size: item.size,
      }));
      setUploadItems(preUploaded);
    }
  }, [value]);

  const acceptedTypes = {
    'image/svg+xml': ['.svg'],
    'image/png': ['.png'],
    'image/jpeg': ['.jpg', '.jpeg'],
    'image/gif': ['.gif'],
    'application/pdf': ['.pdf'],
  };
  const onDrop = useCallback(
    async (acceptedFiles, fileRejections) => {
      const validFiles = acceptedFiles.filter(file => {
        const isValidType = acceptedTypes[file.type];
        const isValidSize = file.size <= MAX_FILE_SIZE;
        return isValidType && isValidSize;
      });

      if (fileRejections.length) {
        showToaster('error', 'Some files were rejected due to invalid type or size.');
        return;
      }

      // Create an upload item for each valid file.
      const newUploads = validFiles.map(file => ({
        file,
        progress: 0,
        status: UploadStatus.UPLOADING,
      }));
      setUploadItems(prevItems => [...prevItems, ...newUploads]);

      // Ensure final-form field value is an array.
      const currentFiles = Array.isArray(value) ? value : [];

      newUploads.forEach(fileItem => {
        const { file } = fileItem;
        const formData = new FormData();
        formData.append('file', file);

        // Simulated incremental progress.
        let simulatedProgress = 0;
        const intervalId = setInterval(() => {
          simulatedProgress += 1;
          if (simulatedProgress < 95) {
            setUploadItems(prevState =>
              prevState.map(up => (up.file === file ? { ...up, progress: simulatedProgress } : up))
            );
          } else {
            clearInterval(intervalId);
          }
        }, 100);
        FirebaseApi.uploadFileToFirebase(formData)
          .then(response => {
            clearInterval(intervalId);
            const { downloadURL, fileName } = response.data;
            // Update local state to mark the file as done and store its FIREBASE key.
            setUploadItems(prevState =>
              prevState.map(up =>
                up.file === file
                  ? {
                      ...up,
                      progress: 100,
                      status: UploadStatus.DONE,
                      key: fileName,
                      fileName: file.name,
                      size: file.size,
                    }
                  : up
              )
            );
            // Update final-form field: append this file's details.
            onChange([
              ...currentFiles,
              {
                fileLink: downloadURL,
                key: fileName,
                date: currentDate,
                name: file.name,
                size: file.size,
              },
            ]);
          })
          .catch(e => {
            clearInterval(intervalId);
            setUploadItems(prevState =>
              prevState.map(up => (up.file === file ? { ...up, status: UploadStatus.ERROR } : up))
            );
          });
      });
    },
    [onChange, value, currentDate]
  );

  // Handle deletion of a file item.
  const handleDelete = fileItem => {
    // Remove the file from local state.
    setUploadItems(prevState => prevState.filter(up => up.file !== fileItem.file));
    // Remove from final-form field if it was uploaded.
    const currentFiles = Array.isArray(value) ? value : [];
    if (fileItem.status === UploadStatus.DONE && fileItem.key) {
      // Call API to delete file from FIREBASE.
      FirebaseApi.deleteFileFromFirebase({ fileName: fileItem.key }).catch(err =>
        showToaster('error', 'Error deleting file from FIREBASE')
      );
      // Remove the entry with matching key from the field.
      const updatedFiles = currentFiles.filter(item => item.key !== fileItem.key);
      onChange(updatedFiles);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    accept: acceptedTypes,
  });

  return (
    <div
      {...getRootProps({
        className: classNames(css.dropzoneWrapper, {
          [css.pointerEvents]: isFinalReviewTab,
        }),
      })}
    >
      {!isFinalReviewTab && (
        <div className={css.dropzone}>
          <input {...getInputProps()} />
          <div className={css.uploadInfo}>
            <span className={css.uploadIcon}>
              <Icons name="upload" />
            </span>{' '}
            <p>
              <strong>
                <FormattedMessage id="MediaUploadField.clickToUpload" />
              </strong>
              <FormattedMessage id="MediaUploadField.dropHere" />
            </p>
            <p>
              <FormattedMessage id="MediaUploadField.dragOrClick" />
            </p>
          </div>
        </div>
      )}
      {uploadItems.length > 0 && (
        <div className={css.filePreview}>
          {uploadItems.map((item, i) => {
            // For pre-uploaded items, item.file will be null, but fileName (or name) is available.
            const displayName = item.file ? item.file.name : item.fileName || 'Uploaded file';
            const extension = extractFileExtension(displayName);
            const filebaseName = getBaseName(displayName);
            return (
              <div key={i} className={css.fileRow}>
                <div className={css.fileInfo}>
                  <div className={css.fileIcon}>
                    <Icons name={extension} />
                  </div>
                  <div className={css.fileDetails}>
                    <div className={css.fileDetailsLeft}>
                      <div className={css.fileName}>{filebaseName}</div>
                      <div className={css.fileSize}>{item.size ? bytesToMB(item.size) : null}</div>
                      <div
                        className={classNames(
                          css.fileStatus,
                          item.status === UploadStatus.ERROR ? css.fileError : null,
                          item.status === UploadStatus.DONE ? css.fileDone : null
                        )}
                      >
                        {item.status === UploadStatus.UPLOADING && 'Uploading...'}
                        {item.status === UploadStatus.DONE && 'Done'}
                        {item.status === UploadStatus.ERROR && 'Error'}
                      </div>
                    </div>
                    {/* Cross icon for deletion */}
                    <button
                      disabled={isFinalReviewTab}
                      type="button"
                      className={css.deleteButton}
                      onClick={e => {
                        e.stopPropagation(); // Prevent click from opening file dialog.
                        handleDelete(item);
                      }}
                      title="Delete file"
                    >
                      <Icons name="bin" />
                    </button>
                  </div>
                </div>
                <div className={css.progressContainer}>
                  <div className={css.progressBar} style={{ width: `${item.progress}%` }} />
                  <span className={css.progressPercentage}>{item.progress}%</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MediaUploadField;
